import React from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { required } from '../../../util/validators';
import { FieldTextInput, Form, Modal, Button } from '../../../components';
import FieldPhoneNumberInput from '../../../components/FieldPhoneNumberInput/FieldPhoneNumberInput';

import { useConfiguration } from '../../../context/configurationContext';

import css from './ContactUsModal.module.css';

const ContactUsForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        disabled,
        handleSubmit,
        intl,
        formId,
        invalid,
        contactSubmitted,
        contactError,
        contactInProgress,
        currentUser
      } = fieldRenderProps;

      const errorMessageMaybe = contactError ? (
        <FormattedMessage id="ContactUsModal.contactSubmitFailed" />
      ) : null;

      const classes = classNames(rootClassName || css.formRoot, className);
      const submitInProgress = contactInProgress;
      const submitDisabled = invalid || disabled || submitInProgress || contactSubmitted;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {!currentUser && (
            <FieldTextInput
              className={css.contactEmail}
              type="email"
              id={formId ? `${formId}.contactEmail` : 'contactEmail'}
              name="contactEmail"
              label={intl.formatMessage({ id: 'ContactUsModal.emailLabel' })}
              placeholder={intl.formatMessage({ id: 'ContactUsModal.emailPlaceholder' })}
              validate={required(intl.formatMessage({ id: 'ContactUsModal.emailRequired' }))}
            />
          )}
          {/* Phone number field */}
          {!currentUser && (
            <FieldPhoneNumberInput
              className={css.phone}
              name="phoneNumber"
              id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
              label={intl.formatMessage({ id: 'ContactDetailsForm.phoneLabel' })}
              placeholder={intl.formatMessage({
                id: 'ContactDetailsForm.phonePlaceholder',
              })}
              validate={null}
            />
          )}
          <FieldTextInput
            className={css.contactMessage}
            type="textarea"
            id={formId ? `${formId}.contactReason` : 'contactReason'}
            name="contactReason"
            label={intl.formatMessage({ id: 'ContactUsModal.label' })}
            placeholder={intl.formatMessage({ id: 'ContactUsModal.contactPlaceholder' })}
            validate={required(intl.formatMessage({ id: 'ContactUsModal.contactReasonRequired' }))}
          />
          <p className={css.errorPlaceholder}>{errorMessageMaybe}</p>
          <Button
            className={css.submitButton}
            type="submit"
            inProgress={contactInProgress}
            disabled={submitDisabled}
            ready={contactSubmitted}
          >
            {intl.formatMessage({ id: 'ContactUsModal.submit' })}
          </Button>
        </Form>
      );
    }}
  />
);

const ContactUsInfo = props => {
  const {
    contactUsDescriptionId,
    descriptionValues = {},
    marketplaceName,
    intl,
    onContactUs,      
    contactSubmitted,
    contactInProgress,
    contactError,
    currentUser
  } = props;
  return (
    <>
      <p className={css.modalTitle}>
        <FormattedMessage id="ContactUsModal.title" 
                          values={{ marketplaceName: marketplaceName }} />
      </p>
      <p className={css.modalMessage}>
        <FormattedMessage id={contactUsDescriptionId} values={descriptionValues} />
      </p>
      <ContactUsForm
        onSubmit={onContactUs}
        contactSubmitted={contactSubmitted}
        contactInProgress={contactInProgress}
        contactError={contactError}
        currentUser={currentUser}
        intl={intl}
      />
    </>
  );
};

const ContactUsSentInfo = ({ marketplaceName })  => (
  <>
    <p className={css.modalTitle}>
      <FormattedMessage id="ContactUsModal.sentTitle" />
    </p>
    <p className={css.modalMessage}>
      <FormattedMessage 
        id="ContactUsModal.sentMessage"
        values={{ marketplaceName }} />
    </p>
  </>
);

const ContactUsErrorInfo = ({ errorNumber, supportEmail }) => (
  <>
    <p className={css.modalTitle}>
      <FormattedMessage id="ContactUsModal.errorTitle" />
    </p>
    <p className={css.modalMessage}>
      <FormattedMessage
        id="ContactUsModal.errorMessage"
        values={{
          errorNumber,
          supportEmail,
        }}
      />
    </p>
  </>
);

const ContactUsModal = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    id,
    isOpen = false,
    onCloseModal,
    onManageDisableScrolling,
    onContactUs,
    contactSubmitted = false,
    contactInProgress = false,
    contactError,
    currentUser,
    contactUsDescriptionId = 'ContactUsModal.description',
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const supportEmail = process.env.REACT_APP_MARKETPLACE_SUPPORT_EMAIL || 'support@securegears.bike';
  const errorNumber = contactError?.status || 'Unknown';
  const marketplaceName = config?.marketplaceName || process.env.REACT_APP_MARKETPLACE_NAME;
  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'ContactUsModal.close' })}
    >
      {contactSubmitted ? (
        <ContactUsSentInfo  marketplaceName={marketplaceName}/>
      ) : contactError ? (
        <ContactUsErrorInfo errorNumber={errorNumber} supportEmail={supportEmail} />
      ) : (
        <ContactUsInfo
          onContactUs={onContactUs}
          contactSubmitted={contactSubmitted}
          contactInProgress={contactInProgress}
          contactError={contactError}
          currentUser={currentUser}
          intl={intl}
          marketplaceName={marketplaceName}
          contactUsDescriptionId={contactUsDescriptionId}
        />
      )}
    </Modal>
  );
};

export default ContactUsModal;